import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface FavoriteItem {
  id: string;
  type: 'tournament' | 'match';
  name: string;
  timestamp: number;
}

interface FavoritesContextType {
  favoriteTournaments: string[];
  favoriteMatches: string[];
  toggleTournamentFavorite: (tournamentId: string) => void;
  toggleMatchFavorite: (matchId: string) => void;
  isTournamentFavorite: (tournamentId: string) => boolean;
  isMatchFavorite: (matchId: string) => boolean;
  getFavoriteItems: () => FavoriteItem[];
  clearFavorites: () => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favoriteTournaments, setFavoriteTournaments] = useState<string[]>([]);
  const [favoriteMatches, setFavoriteMatches] = useState<string[]>([]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    const loadFavorites = () => {
      try {
        const storedTournaments = localStorage.getItem('kridah_favorite_tournaments');
        const storedMatches = localStorage.getItem('kridah_favorite_matches');
        
        if (storedTournaments) {
          setFavoriteTournaments(JSON.parse(storedTournaments));
        }
        if (storedMatches) {
          setFavoriteMatches(JSON.parse(storedMatches));
        }
      } catch (error) {
        console.error('Error loading favorites:', error);
      }
    };

    loadFavorites();
  }, []);

  // Save favorites to localStorage
  const saveFavorites = (tournaments: string[], matches: string[]) => {
    try {
      localStorage.setItem('kridah_favorite_tournaments', JSON.stringify(tournaments));
      localStorage.setItem('kridah_favorite_matches', JSON.stringify(matches));
    } catch (error) {
      console.error('Error saving favorites:', error);
    }
  };

  // Toggle tournament favorite
  const toggleTournamentFavorite = (tournamentId: string) => {
    const newFavorites = favoriteTournaments.includes(tournamentId)
      ? favoriteTournaments.filter(id => id !== tournamentId)
      : [...favoriteTournaments, tournamentId];
    
    setFavoriteTournaments(newFavorites);
    saveFavorites(newFavorites, favoriteMatches);
  };

  // Toggle match favorite
  const toggleMatchFavorite = (matchId: string) => {
    const newFavorites = favoriteMatches.includes(matchId)
      ? favoriteMatches.filter(id => id !== matchId)
      : [...favoriteMatches, matchId];
    
    setFavoriteMatches(newFavorites);
    saveFavorites(favoriteTournaments, newFavorites);
  };

  // Check if tournament is favorite
  const isTournamentFavorite = (tournamentId: string): boolean => {
    return favoriteTournaments.includes(tournamentId);
  };

  // Check if match is favorite
  const isMatchFavorite = (matchId: string): boolean => {
    return favoriteMatches.includes(matchId);
  };

  // Get all favorite items
  const getFavoriteItems = (): FavoriteItem[] => {
    const items: FavoriteItem[] = [];
    
    // Add favorite tournaments
    favoriteTournaments.forEach(id => {
      items.push({
        id,
        type: 'tournament',
        name: `Tournament ${id}`,
        timestamp: Date.now()
      });
    });
    
    // Add favorite matches
    favoriteMatches.forEach(id => {
      items.push({
        id,
        type: 'match',
        name: `Match ${id}`,
        timestamp: Date.now()
      });
    });
    
    return items.sort((a, b) => b.timestamp - a.timestamp);
  };

  // Clear all favorites
  const clearFavorites = () => {
    setFavoriteTournaments([]);
    setFavoriteMatches([]);
    localStorage.removeItem('kridah_favorite_tournaments');
    localStorage.removeItem('kridah_favorite_matches');
  };

  const value: FavoritesContextType = {
    favoriteTournaments,
    favoriteMatches,
    toggleTournamentFavorite,
    toggleMatchFavorite,
    isTournamentFavorite,
    isMatchFavorite,
    getFavoriteItems,
    clearFavorites
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
